import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import GroupButton from 'components/button/groupButton';
import { toast } from 'react-toastify';
import { checkOrgAdmin, getUserOrg, messageErrors } from 'utils/utils';
import { Modal } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import { themModal } from 'utils/theme';
import '../../form/styles.scss';
import MultiSelect from 'components/select/index';
import { orgSelector, userSelector } from 'redux/selectors';
import { useSelector } from 'react-redux';
import { sortByTypeType, defaultpageCount } from 'utils/constants';
import { getLocationByFilter } from 'api/locationApi';
import { reassignPurchaseOrderLocation } from 'api/purchaseOrderApi';

type reassignPOLocationModalProps = {
    openModal: boolean;
    setOpenModal: Function;
    purchaseOrderId: string;
    currentLocationId?: string;
    currentLocationName?: string;
    queryClient?: any;
    onSuccess?: () => void;
};

const ReassignPOLocationModal = (props: reassignPOLocationModalProps) => {
    const { openModal, setOpenModal, purchaseOrderId, currentLocationId, currentLocationName, queryClient, onSuccess } = props;
    const [t] = useTranslation();
    const rootRef = useRef(null);
    const { userInfo } = useSelector(userSelector);
    const { organizationId } = useSelector(orgSelector);
    const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, false);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<any>(null);

    useEffect(() => {
        if (!openModal) return;

        const fetchLocations = async () => {
            const pageSize = defaultpageCount;
            const userOrgId = isOrgAdmin ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId;
            const firstRes = await getLocationByFilter({
                page: 0,
                limit: pageSize,
                sortByType: sortByTypeType.ASC,
                organizationId,
                userOrgId,
                isActive: true,
            });

            const total = firstRes?.data?.totalEntities || 0;
            let allLocations = firstRes?.data?.entities || [];
            const pages = Math.ceil(total / pageSize);

            for (let i = 1; i < pages; i++) {
                const res = await getLocationByFilter({
                    page: i,
                    limit: pageSize,
                    sortByType: sortByTypeType.ASC,
                    organizationId,
                    userOrgId,
                    isActive: true,
                });
                allLocations = allLocations.concat(res?.data?.entities || []);
            }

            const options = allLocations.map((loc: any) => ({ label: loc.name, value: loc.id }));
            setLocationOptions(options);

            if (currentLocationId) {
                const current = options.find((o: any) => o.value === currentLocationId);
                setSelectedLocation(current || null);
            }
        };

        fetchLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal]);

    const handleReassign = async () => {
        if (!selectedLocation?.value) return;
        await reassignPurchaseOrderLocation(purchaseOrderId, selectedLocation.value);
    };

    const mutation = useMutation(['reassign-po-location'], { mutationFn: handleReassign });

    const toggleHandler = () => {
        setOpenModal(false);
        setSelectedLocation(null);
    };

    const onSubmit = () => {
        if (!selectedLocation?.value) {
            const message: string = t('orderPage.reassignLocation.selectRequired');
            toast.error(message);
            return;
        }
        if (selectedLocation.value === currentLocationId) {
            const message: string = t('orderPage.reassignLocation.sameLocation');
            toast.error(message);
            return;
        }
        mutation.mutate(undefined, {
            onSuccess: () => {
                toggleHandler();
                const message: string = t('orderPage.reassignLocation.success', { locationName: selectedLocation.label });
                toast.success(message);
                queryClient?.invalidateQueries();
                onSuccess?.();
            },
            onError: (error: any) => {
                const message: string = messageErrors(error, t);
                toast.error(message);
            },
        });
    };

    return (
        <div ref={rootRef} className="order-modal-container">
            <Modal
                show={openModal}
                size="md"
                className="order-modal"
                root={rootRef.current ?? undefined}
                onClose={toggleHandler}
                dismissible={false}
                theme={themModal}
            >
                <CustomModalHeader title={t('orderPage.reassignLocation.title')} toggle={toggleHandler} />
                <Modal.Body theme={{ base: 'flex-12 pb-4 pt-3 px-8' }}>
                    <div className="py-2">
                        {currentLocationName && (
                            <p className="text-sm text-gray-500 mb-4">
                                {t('orderPage.reassignLocation.currentLocation')}: <span className="font-medium text-gray-700">{currentLocationName}</span>
                            </p>
                        )}
                        <MultiSelect
                            onChange={(option: any) => setSelectedLocation(option)}
                            options={locationOptions}
                            value={selectedLocation}
                            isHolderLabel={true}
                            placeholder={t('orderPage.reassignLocation.selectLocation')}
                            noOptionsMessage={() => t('No options')}
                            closeMenuOnSelect
                        />
                        <p className="text-xs text-gray-400 mt-2">{t('orderPage.reassignLocation.hint')}</p>
                    </div>
                    <GroupButton
                        className="items-center justify-center pt-4 mb-2"
                        buttons={[
                            {
                                type: 'button',
                                text: t('modal.cancel'),
                                classType: 'white',
                                action: toggleHandler,
                            },
                            {
                                type: 'button',
                                text: t('modal.save'),
                                classType: 'blue',
                                isLoading: mutation.isLoading,
                                action: onSubmit,
                            },
                        ]}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ReassignPOLocationModal;
